import React, { useState, useCallback, useEffect } from 'react';
import { Plus, Copy, Upload, X } from 'lucide-react';
import Button from '../../../../components/Button';
import type { QuestTemplateFieldToCollect } from '../../services/questTypes';
import { FieldToCollectDisplay } from './FieldToCollectDisplay';
import { FieldToCollectEdit } from './FieldToCollectEdit';

interface QuestFieldsToCollectManagerProps {
    fields: QuestTemplateFieldToCollect[];
    onChange: (fields: QuestTemplateFieldToCollect[]) => void;
    onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
    className?: string;
}

interface EditingField extends QuestTemplateFieldToCollect {
    isNew?: boolean;
}

const QuestFieldsToCollectManager: React.FC<QuestFieldsToCollectManagerProps> = ({
    fields,
    onChange,
    onUnsavedChangesChange,
    className = ''
}) => {
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingField, setEditingField] = useState<EditingField | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [showImportPopup, setShowImportPopup] = useState(false);
    const [importJsonText, setImportJsonText] = useState('');

    // Notify parent about unsaved changes whenever editing state changes
    useEffect(() => {
        const hasUnsavedChanges = editingIndex !== null && editingField !== null;
        onUnsavedChangesChange?.(hasUnsavedChanges);
    }, [editingIndex, editingField, onUnsavedChangesChange]);

    const validateField = useCallback((field: EditingField, currentIndex?: number): { [key: string]: string } => {
        const fieldErrors: { [key: string]: string } = {};

        if (!field.key.trim()) {
            fieldErrors.key = 'Key is required';
        } else if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(field.key)) {
            fieldErrors.key = 'Key must be a valid identifier (letters, numbers, underscore, no spaces)';
        } else {
            // Check for duplicate keys
            const isDuplicate = fields.some((f, index) =>
                f.key === field.key && index !== currentIndex
            );
            if (isDuplicate) {
                fieldErrors.key = 'Key must be unique';
            }
        }

        if (!field.description.trim()) {
            fieldErrors.description = 'Description is required';
        }

        if (!field.aiGuidance.trim()) {
            fieldErrors.aiGuidance = 'AI Guidance is required';
        }

        if (field.order < 0) {
            fieldErrors.order = 'Order must be 0 or greater';
        }

        return fieldErrors;
    }, [fields]);

    const handleAddField = useCallback(() => {
        const newOrder = fields.length > 0 ? Math.max(...fields.map(f => f.order)) + 1 : 0;
        const newField: EditingField = {
            order: newOrder,
            key: '',
            description: '',
            aiGuidance: '',
            isNew: true
        };
        setEditingField(newField);
        setEditingIndex(fields.length);
        setErrors({});
    }, [fields]);

    const handleEditField = useCallback((index: number) => {
        setEditingField({ ...fields[index] });
        setEditingIndex(index);
        setErrors({});
    }, [fields]);

    const handleSaveField = useCallback((fieldToSave: QuestTemplateFieldToCollect) => {
        if (!editingField) return;

        const fieldErrors = validateField(editingField, editingField.isNew ? undefined : editingIndex!);
        setErrors(fieldErrors);

        if (Object.keys(fieldErrors).length > 0) {
            return;
        }

        const newFields = [...fields];
        const cleanedField: QuestTemplateFieldToCollect = {
            order: fieldToSave.order,
            key: fieldToSave.key.trim(),
            description: fieldToSave.description.trim(),
            aiGuidance: fieldToSave.aiGuidance.trim(),
        };

        if (editingField.isNew) {
            newFields.push(cleanedField);
        } else {
            newFields[editingIndex!] = cleanedField;
        }

        // Sort by order to maintain consistency
        newFields.sort((a, b) => a.order - b.order);

        onChange(newFields);
        setEditingField(null);
        setEditingIndex(null);
        setErrors({});
    }, [editingField, editingIndex, fields, onChange, validateField]);

    const handleCancelEdit = useCallback(() => {
        setEditingField(null);
        setEditingIndex(null);
        setErrors({});
    }, []);

    const handleDeleteField = useCallback((index: number) => {
        const newFields = fields.filter((_, i) => i !== index);
        onChange(newFields);
    }, [fields, onChange]);

    const handleMoveField = useCallback((index: number, direction: 'up' | 'down') => {
        const newFields = [...fields];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex < 0 || targetIndex >= newFields.length) {
            return;
        }

        // Swap the fields
        [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];

        // Update their order values to match their new positions
        newFields.forEach((field, i) => {
            field.order = i;
        });

        onChange(newFields);
    }, [fields, onChange]);

    const handleFieldChange = useCallback((key: keyof EditingField, value: string | number) => {
        if (!editingField) return;

        setEditingField(prev => ({
            ...prev!,
            [key]: value
        }));

        // Clear error for this field when user starts typing
        if (errors[key]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[key];
                return newErrors;
            });
        }
    }, [editingField, errors]);

    const handleCopyFieldsAsJson = useCallback(async () => {
        // Ensure consistent key order: order, key, description, aiGuidance
        const orderedFields = fields.map(field => ({
            order: field.order,
            key: field.key,
            description: field.description,
            aiGuidance: field.aiGuidance
        }));
        const jsonString = JSON.stringify(orderedFields, null, 2);
        await navigator.clipboard.writeText(jsonString);
        // You could add a toast notification here if you have one available
        console.log('Fields copied to clipboard as JSON');
    }, [fields]);

    const validateImportedFields = useCallback((data: unknown): QuestTemplateFieldToCollect[] | null => {
        if (!Array.isArray(data)) {
            console.error('Import error: Data must be an array');
            return null;
        }

        const validatedFields: QuestTemplateFieldToCollect[] = [];

        for (let i = 0; i < data.length; i++) {
            const item = data[i];

            if (!item || typeof item !== 'object') {
                console.error(`Import error: Item at index ${i} is not an object`);
                return null;
            }

            const field = item as Record<string, unknown>;

            // Validate required fields
            if (typeof field.order !== 'number') {
                console.error(`Import error: Item at index ${i} missing or invalid 'order' field`);
                return null;
            }

            if (typeof field.key !== 'string' || !field.key.trim()) {
                console.error(`Import error: Item at index ${i} missing or invalid 'key' field`);
                return null;
            }

            if (typeof field.description !== 'string' || !field.description.trim()) {
                console.error(`Import error: Item at index ${i} missing or invalid 'description' field`);
                return null;
            }

            if (typeof field.aiGuidance !== 'string' || !field.aiGuidance.trim()) {
                console.error(`Import error: Item at index ${i} missing or invalid 'aiGuidance' field`);
                return null;
            }

            // Validate key format
            if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(field.key)) {
                console.error(`Import error: Item at index ${i} has invalid key format: ${field.key}`);
                return null;
            }

            validatedFields.push({
                order: field.order,
                key: field.key.trim(),
                description: field.description.trim(),
                aiGuidance: field.aiGuidance.trim()
            });
        }

        // Check for duplicate keys
        const keys = validatedFields.map(f => f.key);
        const duplicateKeys = keys.filter((key, index) => keys.indexOf(key) !== index);
        if (duplicateKeys.length > 0) {
            console.error(`Import error: Duplicate keys found: ${duplicateKeys.join(', ')}`);
            return null;
        }

        return validatedFields;
    }, []);

    const handleImportFieldsFromJson = useCallback(() => {
        setShowImportPopup(true);
        setImportJsonText('');
    }, []);

    const handleImportFromText = useCallback(() => {
        if (!importJsonText.trim()) {
            alert('Please paste JSON content');
            return;
        }

        try {
            const parsedData = JSON.parse(importJsonText);

            const validatedFields = validateImportedFields(parsedData);
            if (validatedFields) {
                // Sort by order to maintain consistency
                validatedFields.sort((a, b) => a.order - b.order);
                onChange(validatedFields);
                console.log(`Successfully imported ${validatedFields.length} fields`);
                setShowImportPopup(false);
                setImportJsonText('');
            } else {
                alert('Invalid JSON format. Please check the console for details.');
            }
        } catch (error) {
            console.error('Import error: Invalid JSON', error);
            alert('Invalid JSON format. Please check your JSON syntax.');
        }
    }, [importJsonText, onChange, validateImportedFields]);

    const handleCancelImport = useCallback(() => {
        setShowImportPopup(false);
        setImportJsonText('');
    }, []);

    return (
        <div className={`space-y-4 ${className}`}>
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Fields to Collect</h3>
            </div>

            {fields.length === 0 && editingIndex === null && (
                <div className="text-center py-8 text-gray-500">
                    No fields configured. Click "Add Field" to get started.
                </div>
            )}

            <div className="space-y-2">
                {fields.map((field, index) => (
                    <div key={`${field.key}-${index}`} className="border border-gray-200 rounded-lg p-4 bg-white">
                        {editingIndex === index ? (
                            // Edit mode
                            <FieldToCollectEdit
                                field={editingField!}
                                errors={errors}
                                onSave={handleSaveField}
                                onCancel={handleCancelEdit}
                                onFieldChange={handleFieldChange}
                            />
                        ) : (
                            // Display mode
                            <FieldToCollectDisplay
                                isFirst={index === 0}
                                isLast={index === fields.length - 1}
                                field={field}
                                isEditing={editingIndex === index}
                                onMoveUp={() => handleMoveField(index, 'up')}
                                onMoveDown={() => handleMoveField(index, 'down')}
                                onEdit={() => handleEditField(index)}
                                onDelete={() => handleDeleteField(index)}
                            />
                        )}
                    </div>
                ))}

                {/* Add new field form */}
                {editingIndex === fields.length && editingField?.isNew && (
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <FieldToCollectEdit
                            field={editingField}
                            errors={errors}
                            onSave={handleSaveField}
                            onCancel={handleCancelEdit}
                            onFieldChange={handleFieldChange}
                        />
                    </div>
                )}

                <div className="flex flex-wrap items-center gap-2 justify-end">
                    <Button
                        variant="outline"
                        size="sm"
                        icon={<Upload className="w-4 h-4" />}
                        onClick={handleImportFieldsFromJson}
                        disabled={editingIndex !== null}
                        title="Import fields from JSON file"
                    >
                        Import JSON
                    </Button>
                    {fields.length > 0 && (
                        <Button
                            variant="outline"
                            size="sm"
                            icon={<Copy className="w-4 h-4" />}
                            onClick={handleCopyFieldsAsJson}
                            disabled={editingIndex !== null}
                            title="Copy all fields as JSON"
                        >
                            Copy JSON
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        icon={<Plus className="w-4 h-4" />}
                        onClick={handleAddField}
                        disabled={editingIndex !== null}
                    >
                        Add Field
                    </Button>
                </div>
            </div>

            {fields.length > 0 && (
                <div className="text-sm text-gray-500 mt-4">
                    {fields.length} field{fields.length !== 1 ? 's' : ''} configured
                </div>
            )}

            {/* Import JSON Popup */}
            {showImportPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-gray-900">Import Fields from JSON</h3>
                            <button
                                onClick={handleCancelImport}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                title="Close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Paste your JSON here:
                                </label>
                                <textarea
                                    value={importJsonText}
                                    onChange={(e) => setImportJsonText(e.target.value)}
                                    placeholder="Paste your JSON array of fields here..."
                                    className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                                />
                            </div>

                            <div className="text-sm text-gray-600">
                                <p className="mb-2">Expected format:</p>
                                <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                                    {`[
  {
    "order": 0,
    "key": "field_name",
    "description": "Field description",
    "aiGuidance": "AI guidance text"
  }
]`}
                                </pre>
                            </div>

                            <div className="flex items-center gap-3 justify-end pt-4 border-t">
                                <Button
                                    variant="outline"
                                    onClick={handleCancelImport}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleImportFromText}
                                    disabled={!importJsonText.trim()}
                                >
                                    Import Fields
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuestFieldsToCollectManager;
